import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { catchError, map, Observable, timeout } from 'rxjs';
import { ApiClientConfig } from '../api-client.config';
import {
  GetPinnedPageParams,
  GetPinnedPageResponse,
  GetSearchPageResponse,
  PageInfoResponse,
  PinnedPageBodyModel,
  PinPageResponse,
  SearchPageParams
} from './api.fanpage.models';

export class PageApi {
  private apiUrl = environment.API_URL;
  PAGE = '/page/pages';

  constructor(
    public readonly http: HttpClient,
    public config: ApiClientConfig
  ) {}

  getFanpageInfo(fanpageId: string | null): Observable<PageInfoResponse> {
    return this.http.get<PageInfoResponse>(`${this.apiUrl}${this.PAGE}/${fanpageId}`).pipe(
      timeout(this.config.responseTimeout),
      map(response => {
        return response;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getFanpageSummary(startDateEpochMillis: number, endDateEpochMillis: number): Observable<any> {
    return this.http
      .get<any>(
        `${this.apiUrl}/page/management/v2?startDateEpochMillis=${startDateEpochMillis}&endDateEpochMillis=${endDateEpochMillis}`
      )
      .pipe(
        timeout(60000),
        map(response => {
          return response;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getFanpageGrowth(
    viewType: string,
    startDateEpochMillis: number,
    endDateEpochMillis: number
  ): Observable<any> {
    return this.http
      .get<any>(
        `${this.apiUrl}/page/management/page-growth?viewType=${viewType}&startDateEpochMillis=${startDateEpochMillis}&endDateEpochMillis=${endDateEpochMillis}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map(response => {
          return response;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getFanpagePostGrowth(
    viewType: string,
    startDateEpochMillis: number,
    endDateEpochMillis: number
  ): Observable<any> {
    return this.http
      .get<any>(
        `${this.apiUrl}/page/management/post-growth?viewType=${viewType}&startDateEpochMillis=${startDateEpochMillis}&endDateEpochMillis=${endDateEpochMillis}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map(response => {
          return response;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getVerifiedFanpageGrowth(
    viewType: string,
    startDateEpochMillis: number,
    endDateEpochMillis: number
  ): Observable<any> {
    return this.http
      .get<any>(
        `${this.apiUrl}/page/management/verification-growth?viewType=${viewType}&startDateEpochMillis=${startDateEpochMillis}&endDateEpochMillis=${endDateEpochMillis}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map(response => {
          return response;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getFanpageTopCategory(startDateEpochMillis: number, endDateEpochMillis: number): Observable<any> {
    return this.http
      .get<any>(
        `${this.apiUrl}/page/pages/top-category?startDateEpochMillis=${startDateEpochMillis}&endDateEpochMillis=${endDateEpochMillis}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map(response => {
          return response;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getTopFanpage(
    type: string,
    startDateEpochMillis: number,
    endDateEpochMillis: number,
    count: number
  ): Observable<any> {
    return this.http
      .get<any>(
        `${this.apiUrl}/page/management/top-fanpage?type=${type}&startDateEpochMillis=${startDateEpochMillis}&endDateEpochMillis=${endDateEpochMillis}&count=${count}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map(response => {
          return response;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getTopFanpagePost(
    type: string,
    startDateEpochMillis: number,
    endDateEpochMillis: number,
    count: number
  ): Observable<any> {
    return this.http
      .get<any>(
        `${this.apiUrl}/page/management/top-fanpage-post?type=${type}&startDateEpochMillis=${startDateEpochMillis}&endDateEpochMillis=${endDateEpochMillis}&count=${count}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map(response => {
          return response;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getPinnedFanpage(params: GetPinnedPageParams): Observable<GetPinnedPageResponse> {
    return this.http
      .get<GetPinnedPageResponse>(`${this.apiUrl}/page/pages/whitelist/${params.privilege}`, {
        params: { pageNum: params.pageNum, pageSize: params.pageSize }
      })
      .pipe(
        timeout(this.config.responseTimeout),
        map(response => {
          return response;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  searchFanpage(params: SearchPageParams): Observable<GetSearchPageResponse> {
    return this.http
      .get<GetSearchPageResponse>(`${this.apiUrl}/page/management/search`, {
        params: { ...params }
      })
      .pipe(
        timeout(this.config.responseTimeout),
        map(response => {
          return response;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  pinnedPage(body: PinnedPageBodyModel[]): Observable<PinPageResponse> {
    return this.http
      .post<PinPageResponse>(`${this.apiUrl}/page/management/privileges/pinned-pages`, body)
      .pipe(
        timeout(this.config.responseTimeout),
        map(response => {
          return response;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getPinnedPageWhitelist(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/feeds-engine/priority-object`).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res;
      }),
      catchError(err => {
        throw err.error;
      })
    );
  }

  getUploadPrivileges(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/page/pages/upload-privileges`).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res;
      }),
      catchError(err => {
        throw err.error;
      })
    );
  }

  getPinnedPageWhitelistUploadFile(): Observable<any> {
    return this.http
      .get<any>(
        `${this.apiUrl}/page/pages/privileges/uploads?privilege=1gb_video_upload&pageNum=0&pageSize=25`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res;
        }),
        catchError(err => {
          throw err.error;
        })
      );
  }

  pinnedPageWhitelist(body: PinnedPageBodyModel[]): Observable<any> {
    const params = body.map((item): any => ({
      object_id: item.page_id,
      object_type: 'page'
    }));
    return this.http.post<any>(`${this.apiUrl}/feeds-engine/priority-object`, params).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res;
      }),
      catchError(err => {
        throw err.error;
      })
    );
  }

  pinnedPageWhitelistUploadFile(page_id: string, privilege: string): Observable<any> {
    return this.http
      .put(`${this.apiUrl}/page/pages/privileges/uploads/${page_id}/${privilege}`, {})
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res;
        }),
        catchError(err => {
          throw err.error;
        })
      );
  }

  removePinnedPage(pageId: string, privilege: string = 'suggest_new_feed'): Observable<any> {
    return this.http
      .delete<any>(`${this.apiUrl}/page/management/${pageId}/privileges/${privilege}`)
      .pipe(
        timeout(this.config.responseTimeout),
        map(response => {
          return response;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  removePinnedPageDataWhitelist(pageId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/feeds-engine/priority-object/${pageId}`).pipe(
      timeout(this.config.responseTimeout),
      map(response => {
        return response;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  removePinnedPageDataWhitelistUploadFile(pageId: string, privilege: string): Observable<any> {
    return this.http
      .delete<any>(`${this.apiUrl}/page/pages/privileges/uploads/${pageId}/${privilege}`)
      .pipe(
        timeout(this.config.responseTimeout),
        map(response => {
          return response;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }
}
